import { GetPermitHistoryResponseBodyStatusDTO, PermitHistoryDTO } from '@ketch-com/figurehead'
import { useGetOrInitiatePermitHistory } from 'api/historicalPermits/queries/useGetOrInitiatePermitHistory'
import { usePollPermitHistory } from 'api/historicalPermits/queries/usePollPermitHistory'
import { HistoricalPermitPopupVariant } from 'pages/insights/auditLog/components/historicalPermitPopup/constants'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { useEffect, useMemo, useState } from 'react'

export const useHistoricalPermits = (variant: HistoricalPermitPopupVariant) => {
  const { queries, consentTransactionData, subscriptionTransactionData, auditLogHistoricalPermitSubscriptionCode } =
    useAuditLogContext()

  const [isReady, setIsReady] = useState(false)
  const [permitHistory, setPermitHistory] = useState<PermitHistoryDTO[]>([])

  // BE wants the latest transaction ID for a given identifier, and it will still return permits from
  // multiple transactions.
  const latestTransactionId = useMemo(() => {
    const transactions =
      variant === HistoricalPermitPopupVariant.Consent ? consentTransactionData : subscriptionTransactionData
    return transactions.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0]?.transactionID
  }, [variant, consentTransactionData, subscriptionTransactionData])

  // Get the permit history for this identifier if already pulled, or initiate a new pull
  const { data: permitHistoryResponse, isLoading: isLoadingGetOrInitiate } = useGetOrInitiatePermitHistory({
    params: {
      identifierCode: queries.identitySpaceCode,
      identifierValue: queries.identifierSearch || '',
      transactionID: latestTransactionId || '',
      permitType: variant === HistoricalPermitPopupVariant.Consent ? 'purpose' : 'subscription_topic',
    },
  })

  // If the permit history isn't ready, call the poll endpoint. This refetch is called every 3 seconds in the Popup component
  const {
    data: permitHistoryPollResponse,
    isLoading: isLoadingPoll,
    refetch: refetchPoll,
  } = usePollPermitHistory({
    enabled:
      !!permitHistoryResponse?.permitHistoryID &&
      permitHistoryResponse?.status !==
        GetPermitHistoryResponseBodyStatusDTO.CompletedGetPermitHistoryResponseBodyStatus,
    params: {
      historyId: permitHistoryResponse?.permitHistoryID || '',
    },
  })

  useEffect(() => {
    let newPermitHistory: PermitHistoryDTO[] | undefined

    if (
      permitHistoryResponse?.status ===
        GetPermitHistoryResponseBodyStatusDTO.CompletedGetPermitHistoryResponseBodyStatus &&
      permitHistoryResponse?.history
    ) {
      newPermitHistory = permitHistoryResponse.history
    } else if (
      permitHistoryPollResponse?.status ===
        GetPermitHistoryResponseBodyStatusDTO.CompletedGetPermitHistoryResponseBodyStatus &&
      permitHistoryPollResponse?.history
    ) {
      newPermitHistory = permitHistoryPollResponse.history
    }

    if (newPermitHistory && variant === HistoricalPermitPopupVariant.Subscription) {
      const filteredPermitHistory = newPermitHistory.reduce((acc, item) => {
        const filteredProperties = item.permitProperties?.filter(
          property => property?.topicCode === auditLogHistoricalPermitSubscriptionCode,
        )
        if (filteredProperties?.length) {
          acc.push({
            ...item,
            permitProperties: filteredProperties,
          })
        }
        return acc
      }, [] as PermitHistoryDTO[])
      if (
        permitHistoryPollResponse?.status ===
          GetPermitHistoryResponseBodyStatusDTO.CompletedGetPermitHistoryResponseBodyStatus ||
        newPermitHistory?.length
      ) {
        setPermitHistory(filteredPermitHistory)
        setIsReady(true)
      }
    } else if (
      permitHistoryPollResponse?.status ===
        GetPermitHistoryResponseBodyStatusDTO.CompletedGetPermitHistoryResponseBodyStatus ||
      newPermitHistory?.length
    ) {
      setPermitHistory(newPermitHistory || [])
      setIsReady(true)
    }
  }, [permitHistoryResponse, permitHistoryPollResponse, auditLogHistoricalPermitSubscriptionCode, variant])

  return {
    permitHistory,
    isLoadingGetOrInitiate,
    isLoadingPoll,
    refetchPoll,
    isReady,
  }
}
